import { Notification } from '@/components/Notification/Notification';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { InternalOrganizationBillingDetailsType } from '@/api/v1/organization/internal/getInternalOrganizationBillingDetailsApi';
import { getMasterAdminBillingEventsPath } from '../../_BillingEventsPage/path/path';

type PlanLimitsNotificationsType = {
  billingDetails: InternalOrganizationBillingDetailsType;
};

export const PlanLimitsNotifications = ({ billingDetails }: PlanLimitsNotificationsType) => {
  const { monthly_views_available, extra_views_available, extra_views_bought, current_plan } = billingDetails;
  const { monthly_views } = current_plan || {};
  const isMonthlyPaymentsLimitReached = monthly_views_available === 0;
  const hasAvailableOverageWires = !!extra_views_available;
  const hasPurchasedOverageWires = extra_views_bought > 0;

  if (isMonthlyPaymentsLimitReached && hasPurchasedOverageWires && !hasAvailableOverageWires) {
    return <UsedOverageWiresLimitNotification />;
  }

  if (isMonthlyPaymentsLimitReached && (extra_views_bought === 0 || !hasAvailableOverageWires)) {
    return <UsedMonthlyLimitNotification />;
  }

  if (isMonthlyPaymentsLimitReached && hasAvailableOverageWires) {
    return <OverageWiresAddedNotification />;
  }

  if (!isMonthlyPaymentsLimitReached && hasAvailableOverageWires) {
    return <HasMonthlyAndOverageWiresLimitNotification />;
  }

  return <UsedLimitsNotification activeCount={monthly_views - monthly_views_available} totalCount={monthly_views} />;
};

type UsedLimitsNotification = {
  activeCount: number;
  totalCount: number;
};

export const UsedLimitsNotification = ({ activeCount, totalCount }: UsedLimitsNotification) => {
  return (
    <Notification>
      <Typography variant='bodySmall' color={COLORS.grey[950]} fontWeight='semibold'>
        You`re working within your limit.
      </Typography>
      {`You\`ve used ${activeCount} of ${totalCount} wires and/or receiving. You\`ll be able to purchase more once you reach the limit.`}
    </Notification>
  );
};

export const UsedMonthlyLimitNotification = () => {
  return (
    <Notification variant='info-secondary'>
      <Typography variant='bodySmall' color={COLORS.grey[950]} fontWeight='semibold'>
        You`ve used all wires included in your monthly limit.
      </Typography>
      Admins of your organization will not be able to view wire info provided by Recipient for new sending and assign
      Depositors for new receiving.{' '}
      <NavigationLink to={getMasterAdminBillingEventsPath()}>{'View details >'}</NavigationLink>
    </Notification>
  );
};

export const OverageWiresAddedNotification = () => {
  return (
    <Notification>
      <Typography variant='bodySmall' color={COLORS.grey[950]} fontWeight='semibold'>
        Overage wires are added.
      </Typography>
      Your organization can continue working without any limitations until your overage wires are used.
    </Notification>
  );
};

export const UsedOverageWiresLimitNotification = () => {
  return (
    <Notification variant='info-secondary'>
      <Typography variant='bodySmall' color={COLORS.grey[950]} fontWeight='semibold'>
        You`ve used all overage wires.
      </Typography>
      Admins of your organization will not be able to view wire info provided by Recipient for new sending and assign
      Depositors for new receiving.
    </Notification>
  );
};

export const HasMonthlyAndOverageWiresLimitNotification = () => {
  return (
    <Notification>
      <Typography variant='bodySmall' color={COLORS.grey[950]} fontWeight='semibold'>
        You`re working within your limit.
      </Typography>
      Your monthly included wires are always used first to ensure you get the most value from your subscription. Overage
      wires remain in your account until needed.
    </Notification>
  );
};
